import { useEffect, useCallback, useRef, useState } from 'react';
import { Vec3, type CameraOptions } from '@mindcloudxai_3d/core';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useViewerStore } from '@stores/viewerStore';
import { useAssetLibraryStore } from '@stores/assetLibraryStore';
import { sceneBaseRotation } from '@/pages/explore/utils/sceneRotation';
import { RendererController } from '@/renderer/controller';
import { replayModels } from '@/modules/asset-library/modelSync';
import { useRaceTrackStore } from '../useRaceTrackStore';
import { TrackToolbar } from './TrackToolbar';
import { TrackTimeline } from './TrackTimeline';
import { TrackExportDialog } from './TrackExportDialog';
import type { RaceKeyframe, TrackExportConfig } from '../types';
import { raceKeyframesToTrack } from '../keyframeUtils';

interface TrackEditorProps {
  getController: () => RendererController | null;
}

const EXPORT_RESOLUTION: Record<
  Exclude<TrackExportConfig['resolution'], 'original'>,
  [number, number]
> = {
  'square-1080': [1080, 1080],
  '720p': [1280, 720],
  '1080p': [1920, 1080],
};

const EXPORT_BITRATE: Record<TrackExportConfig['quality'], number> = {
  standard: 5_000_000,
  high: 8_000_000,
  ultra: 12_000_000,
};

const EXPORT_CODEC_MIME: Record<TrackExportConfig['codec'], string[]> = {
  h264: ['video/mp4;codecs=avc1.42E01E', 'video/mp4', 'video/webm;codecs=h264'],
  vp9: ['video/webm;codecs=vp9', 'video/webm'],
  vp8: ['video/webm;codecs=vp8', 'video/webm'],
};

function resolveExportSize(resolution: TrackExportConfig['resolution']): [number, number] {
  if (resolution !== 'original') return EXPORT_RESOLUTION[resolution];
  return [window.innerWidth, window.innerHeight];
}

function resolveExportMimeType(codec: TrackExportConfig['codec']): string {
  if (typeof MediaRecorder === 'undefined') {
    throw new Error(i18n.t('raceTrack.mediaRecorderUnsupported'));
  }
  return (
    EXPORT_CODEC_MIME[codec].find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) ??
    'video/webm'
  );
}

function exportFileExtension(mimeType: string): 'mp4' | 'webm' {
  return mimeType.startsWith('video/mp4') ? 'mp4' : 'webm';
}

// 计算偏移视角：不直接跳到帧相机位置（会与 gizmo 重合导致遮挡），
// 而是偏移到帧相机右侧看向帧位置，使 gizmo 在视野中可见
function offsetViewPose(position: Vec3, target: Vec3, fov: number) {
  const dir = target.sub(position);
  const dist = dir.length();
  const dirN = dir.normalize(Vec3.FORWARD);
  const right = dirN.cross(Vec3.UP).normalize(new Vec3(1, 0, 0));
  const offset = Math.max(dist * 0.5, 1.5);
  const viewPos = position.add(right.scale(offset));
  return { position: viewPos, target: position, fov };
}

function createExportContainer(width: number, height: number): HTMLDivElement {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-100000px';
  container.style.top = '0';
  container.style.width = `${width}px`;
  container.style.height = `${height}px`;
  container.style.overflow = 'hidden';
  container.style.pointerEvents = 'none';
  document.body.appendChild(container);
  return container;
}

/**
 * 创建 ready 等待 Promise，必须在 loadScene 之前调用以避免竞态。
 * supersplat-viewer 在 LOD 全部加载后触发 firstFrame → ready。
 */
function createReadyPromise(controller: RendererController, timeoutMs = 60000): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      controller.off('ready', onReady);
      reject(new Error(i18n.t('raceTrack.sceneTimeout')));
    }, timeoutMs);
    const onReady = () => {
      clearTimeout(timer);
      resolve();
    };
    controller.on('ready', onReady);
  });
}

export function TrackEditor({ getController }: TrackEditorProps) {
  const { t } = useTranslation();
  const isEditing = useRaceTrackStore((s) => s.isEditing);
  const keyframes = useRaceTrackStore((s) => s.keyframes);
  const duration = useRaceTrackStore((s) => s.duration);
  const isPlaying = useRaceTrackStore((s) => s.isPlaying);
  const selectedKeyframeId = useRaceTrackStore((s) => s.selectedKeyframeId);
  const originalTrack = useRaceTrackStore((s) => s.originalTrack);

  const setEditing = useRaceTrackStore((s) => s.setEditing);
  const setPlaying = useRaceTrackStore((s) => s.setPlaying);
  const setPreviewOpen = useRaceTrackStore((s) => s.setPreviewOpen);
  const setSelectedKeyframeId = useRaceTrackStore((s) => s.setSelectedKeyframeId);
  const appendKeyframe = useRaceTrackStore((s) => s.appendKeyframe);
  const updateKeyframePose = useRaceTrackStore((s) => s.updateKeyframePose);
  const deleteKeyframe = useRaceTrackStore((s) => s.deleteKeyframe);
  const clearKeyframes = useRaceTrackStore((s) => s.clearKeyframes);
  const optimizeTrack = useRaceTrackStore((s) => s.optimizeTrack);
  const restoreTrack = useRaceTrackStore((s) => s.restoreTrack);
  const backupKeyframes = useRaceTrackStore((s) => s.backupKeyframes);

  const editorTime = useRaceTrackStore((s) => s.editorTime);
  const setEditorTime = useRaceTrackStore((s) => s.setEditorTime);
  const sceneConfig = useViewerStore((s) => s.sceneConfig);
  const taskData = useViewerStore((s) => s.taskData);
  const assets = useAssetLibraryStore((s) => s.assets);
  const playheadTimeRef = useRef(editorTime);
  const playheadCameraFallbackRef = useRef<CameraOptions>({
    position: { x: 0, y: 1.5, z: 4 },
    target: { x: 0, y: 0, z: 0 },
    fov: 60,
  });
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    playheadTimeRef.current = editorTime;
  }, [editorTime]);

  const handleSeek = useCallback(
    (time: number) => {
      const clamped = Math.max(0, Math.min(time, duration));
      playheadTimeRef.current = clamped;
      setEditorTime(clamped);
    },
    [duration, setEditorTime],
  );

  // 1. 捕获当前机位
  const handleCapture = useCallback(() => {
    const controller = getController();

    try {
      const pose = controller?.camera.get() ?? playheadCameraFallbackRef.current;
      playheadCameraFallbackRef.current = pose;
      let captureTime = Math.max(0, Math.min(playheadTimeRef.current, duration));
      while (
        keyframes.some((kf) => Math.abs(kf.time - captureTime) <= 0.05) &&
        captureTime < duration
      ) {
        captureTime = Math.min(duration, captureTime + 0.5);
      }
      if (keyframes.some((kf) => Math.abs(kf.time - captureTime) <= 0.05)) {
        captureTime = 0;
        while (
          keyframes.some((kf) => Math.abs(kf.time - captureTime) <= 0.05) &&
          captureTime < duration
        ) {
          captureTime = Math.min(duration, captureTime + 0.5);
        }
      }
      playheadTimeRef.current = captureTime;
      setEditorTime(captureTime);
      appendKeyframe(captureTime, pose);
    } catch (err) {
      console.error('Failed to capture camera pose:', err);
    }
  }, [duration, getController, keyframes, appendKeyframe, setEditorTime]);

  // 2. 播放/暂停控制集
  // 只切换 store 状态，实际播放由 TrackPreview 响应 isPlaying 在自己的 controller 上执行
  const handlePlayToggle = useCallback(() => {
    if (keyframes.length < 2) return;
    setPlaying(!isPlaying);
  }, [isPlaying, setPlaying, keyframes]);

  // 3. 选中某个关键帧时的相机跳变
  // 不直接跳到帧相机位置（会与 gizmo 重合导致遮挡），
  // 而是偏移到旁边看向帧相机位置，使 gizmo 在视野中可见
  const handleKeyframeSelect = useCallback(
    (kf: RaceKeyframe) => {
      setSelectedKeyframeId(kf.id);

      const controller = getController();
      if (!controller) return;

      controller.camera.set(offsetViewPose(kf.position, kf.target, kf.fov));

      handleSeek(kf.time);
    },
    [getController, handleSeek, setSelectedKeyframeId],
  );

  const handleUpdateSelected = useCallback(() => {
    if (!selectedKeyframeId) return;
    const controller = getController();
    if (!controller) return;
    const pose = controller.camera.get();
    updateKeyframePose(selectedKeyframeId, {
      position: new Vec3(pose.position.x, pose.position.y, pose.position.z),
      target: new Vec3(pose.target.x, pose.target.y, pose.target.z),
      fov: pose.fov,
    });
    // 更新后取消选中，gizmo effect 自动移除 gizmo，避免与相机重合遮挡
    setSelectedKeyframeId(null);
  }, [getController, selectedKeyframeId, updateKeyframePose, setSelectedKeyframeId]);

  const handleDeleteSelected = useCallback(() => {
    if (selectedKeyframeId) deleteKeyframe(selectedKeyframeId);
  }, [deleteKeyframe, selectedKeyframeId]);

  const handleDeleteAll = useCallback(() => {
    clearKeyframes();
  }, [clearKeyframes]);

  // 优化/取消优化切换：有备份时为取消优化，无备份时为执行优化
  const handleOptimize = useCallback(() => {
    if (backupKeyframes) {
      restoreTrack();
    } else {
      optimizeTrack();
    }
  }, [backupKeyframes, optimizeTrack, restoreTrack]);

  const handleJumpToStart = useCallback(() => {
    handleSeek(0);
  }, [handleSeek]);

  const handleJumpToEnd = useCallback(() => {
    handleSeek(duration);
  }, [duration, handleSeek]);

  const handleJumpToPrevious = useCallback(() => {
    const previous = keyframes
      .toSorted((a, b) => b.time - a.time)
      .find((kf) => kf.time < editorTime - 0.01);
    handleSeek(previous?.time ?? 0);
  }, [editorTime, handleSeek, keyframes]);

  const handleJumpToNext = useCallback(() => {
    const next = keyframes
      .toSorted((a, b) => a.time - b.time)
      .find((kf) => kf.time > editorTime + 0.01);
    handleSeek(next?.time ?? duration);
  }, [editorTime, duration, handleSeek, keyframes]);

  const handleExport = useCallback(
    async (config: TrackExportConfig) => {
      if (!sceneConfig) throw new Error(t('raceTrack.sceneNotReady'));
      if (keyframes.length < 2) throw new Error(t('raceTrack.needTwoKeyframes'));

      const { fps, resolution, codec, quality, onProgress } = config;
      const [width, height] = resolveExportSize(resolution);
      const frameIntervalMs = 1000 / fps;
      // 渲染 0..N 共 N+1 帧，帧 i 的 time = min(i/fps, duration)
      // N 个间隔 × frameIntervalMs = duration，视频总时长精确等于 duration
      const totalFrames = Math.round(duration * fps);
      const exportContainer = createExportContainer(width, height);
      const exportController = new RendererController();
      const recordCanvas = document.createElement('canvas');
      recordCanvas.width = width;
      recordCanvas.height = height;
      const recordCtx = recordCanvas.getContext('2d');
      if (!recordCtx) throw new Error(t('raceTrack.exportCanvasFailed'));
      let recordStream: MediaStream | null = null;
      const sortedKeyframes = keyframes.toSorted((a, b) => a.time - b.time);
      const track = raceKeyframesToTrack(sortedKeyframes, duration, 'none');

      try {
        await exportController.init({
          container: exportContainer,
          background: '#020617',
          renderer: 'playcanvas',
          deviceType: 'webgl',
          avatarEnabled: false,
          avatarFollowCamera: false,
          inputEnabled: false,
        });

        // 在 loadScene 之前注册 ready 监听，避免 firstFrame 已触发后注册导致超时
        const readyPromise = createReadyPromise(exportController);

        await exportController.loadScene({
          url: sceneConfig.asset.url,
          sogMeta: sceneConfig.asset.sogMeta,
          settings: sceneConfig.experienceSettings,
          animTrack: track,
          autoPlayAnim: false,
          baseRotation: sceneBaseRotation(taskData ? (taskData.is_yup ?? false) : true),
        });

        // 等待 LOD 加载完成 + 首帧渲染
        await readyPromise;

        // 重放主视窗中的模型到导出 controller
        await replayModels(exportController, assets);

        // 与 demo 对齐：先 setTrack → setMode → pause，再 resize + 等待
        exportController.navigation.anim.setTrack(track);
        exportController.navigation.setMode('anim');
        exportController.navigation.anim.pause();

        // resize 后等一帧让 framebuffer 同步生效
        exportController.resize(width, height);
        await new Promise((r) => setTimeout(r, 200));

        const mimeType = resolveExportMimeType(codec);
        recordStream = recordCanvas.captureStream(0);
        const captureTrack = recordStream.getVideoTracks()[0] as CanvasCaptureMediaStreamTrack;
        const recorder = new MediaRecorder(recordStream, {
          mimeType,
          videoBitsPerSecond: EXPORT_BITRATE[quality],
        });
        const chunks: Blob[] = [];
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };
        const done = new Promise<Blob>((resolve) => {
          recorder.onstop = () => resolve(new Blob(chunks, { type: mimeType }));
        });

        // 阶段 1：尽快渲染所有帧，存为 JPEG Blob（不受 fps 限制）
        const frameBlobs: Blob[] = [];
        for (let frame = 0; frame <= totalFrames; frame += 1) {
          const time = Math.min(frame / fps, duration);
          exportController.navigation.anim.seek(time);
          exportController.flushFrame(0);
          const blob = await exportController.camera.captureScreenshot({
            mimeType: 'image/jpeg',
            quality: 0.9,
          });
          frameBlobs.push(blob);
          onProgress?.((0.5 * frame) / totalFrames);
        }

        // 阶段 2：以精确 fps 间隔回放帧到录制 canvas
        // 每帧仅 drawImage + requestFrame（~2ms），远小于 frameIntervalMs，墙钟 pacing 精确
        const drawBlob = async (blob: Blob) => {
          const bitmap = await createImageBitmap(blob);
          recordCtx.clearRect(0, 0, width, height);
          recordCtx.drawImage(bitmap, 0, 0, width, height);
          bitmap.close();
        };

        // 预绘制第 0 帧，确保 start 时 canvas 有内容
        await drawBlob(frameBlobs[0]);

        recorder.start();
        captureTrack.requestFrame?.();
        const recordingStart = performance.now();

        for (let frame = 1; frame <= totalFrames; frame += 1) {
          await drawBlob(frameBlobs[frame]);
          captureTrack.requestFrame?.();
          onProgress?.(0.5 + (0.5 * frame) / totalFrames);

          const targetTime = recordingStart + frame * frameIntervalMs;
          const waitMs = targetTime - performance.now();
          if (waitMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, waitMs));
          }
        }

        // 精确等待到 duration 时刻再 stop
        const stopWait = recordingStart + totalFrames * frameIntervalMs - performance.now();
        if (stopWait > 0) {
          await new Promise((resolve) => setTimeout(resolve, stopWait));
        }
        recorder.stop();
        const resultBlob = await done;
        const ext = exportFileExtension(mimeType);
        const url = URL.createObjectURL(resultBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `race-track-export-${width}x${height}.${ext}`;
        a.click();
        URL.revokeObjectURL(url);
        onProgress?.(1);
      } finally {
        recordStream?.getTracks().forEach((t) => t.stop());
        exportController.dispose();
        exportContainer.remove();
      }
    },
    [duration, keyframes, sceneConfig, taskData, assets],
  );

  // 选中关键帧时在主视窗中显示 gizmo，拖拽 gizmo 实时更新关键帧 position
  // 注意：依赖数组不含 keyframes，避免拖拽时 updateKeyframePose → keyframes 变化
  // → effect 重跑 → gizmo 被销毁重建导致拖拽中断。keyframe 数据通过 ref 获取最新值。
  const keyframesRef = useRef(keyframes);
  keyframesRef.current = keyframes;

  useEffect(() => {
    const controller = getController();
    if (!controller || !isEditing) return;

    if (!selectedKeyframeId) {
      controller.scene.setCameraGizmo?.(null);
      return;
    }

    const kf = keyframesRef.current.find((k) => k.id === selectedKeyframeId);
    if (!kf) {
      controller.scene.setCameraGizmo?.(null);
      return;
    }

    // 在关键帧相机位置显示 translate gizmo
    controller.scene.setCameraGizmo?.(
      { position: kf.position, target: kf.target, fov: kf.fov },
      'translate',
    );

    const onGizmoTransform = (pos: { x: number; y: number; z: number }) => {
      const newPos = new Vec3(pos.x, pos.y, pos.z);
      updateKeyframePose(selectedKeyframeId, { position: newPos });
    };
    controller.on('cameraGizmo:transform', onGizmoTransform);

    return () => {
      controller.off('cameraGizmo:transform', onGizmoTransform);
      controller.scene.setCameraGizmo?.(null);
    };
  }, [getController, isEditing, selectedKeyframeId, updateKeyframePose]);

  // 编辑期间在主视窗中显示运镜轨迹线
  useEffect(() => {
    if (!isEditing) {
      getController()?.scene.showAnimTrackRoute(null);
      return;
    }
    if (keyframes.length === 0) {
      getController()?.scene.showAnimTrackRoute(null);
      return;
    }
    if (keyframes.length === 1) {
      const kf = keyframes[0];
      getController()?.scene.showCameraTrajectory([
        { position: kf.position, target: kf.target, fov: kf.fov },
      ]);
      return;
    }
    getController()?.scene.showAnimTrackRoute(raceKeyframesToTrack(keyframes, duration, 'repeat'));
  }, [duration, isEditing, keyframes, getController]);

  // 4. 清除与关闭
  const handleClose = useCallback(() => {
    setEditing(false);
    setPlaying(false);
    setPreviewOpen(false);

    const controller = getController();
    if (controller) {
      controller.navigation.anim.pause();
      controller.navigation.anim.setTrack(originalTrack);
      controller.navigation.setMode('orbit');
      controller.scene.showAnimTrackRoute(null);
      controller.scene.setCameraGizmo?.(null);
    }
    // 编辑器不再操作 cinematicState，退出时重置为 idle 避免主工具栏播放按钮状态错乱
    useViewerStore.getState().setCinematicState('idle');
  }, [setEditing, setPlaying, setPreviewOpen, getController, originalTrack]);

  useEffect(() => {
    if (!isEditing) return;

    const controller = getController();
    if (!controller) return;

    // 进入编辑时默认打开预览窗口，重置编辑器时间为 0
    setPreviewOpen(true);
    setEditorTime(0);
    controller.navigation.anim.pause();
    controller.navigation.setMode('fly');
    useViewerStore.getState().setNavigationMode('fly');
    setPlaying(false);
  }, [getController, isEditing, setPlaying, setPreviewOpen, setEditorTime]);

  // 用 ref 持有最新回调，避免 effect 随回调引用变化而重新订阅
  const shortcutsRef = useRef({
    handlePlayToggle,
    handleCapture,
    handleUpdateSelected,
    handleDeleteSelected,
    handleJumpToStart,
    handleJumpToPrevious,
    handleJumpToNext,
    handleJumpToEnd,
  });
  shortcutsRef.current = {
    handlePlayToggle,
    handleCapture,
    handleUpdateSelected,
    handleDeleteSelected,
    handleJumpToStart,
    handleJumpToPrevious,
    handleJumpToNext,
    handleJumpToEnd,
  };

  // 5. 键盘快捷键监听（空格键播放/暂停）
  useEffect(() => {
    if (!isEditing) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // 如果焦点在输入框中，不触发编辑器快捷键
      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA'
      ) {
        return;
      }

      const runEditorShortcut = (action: () => void) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        action();
      };

      const s = shortcutsRef.current;
      if (e.code === 'Space') runEditorShortcut(s.handlePlayToggle);
      if (e.code === 'KeyF') runEditorShortcut(s.handleCapture);
      if (e.code === 'KeyU') runEditorShortcut(s.handleUpdateSelected);
      if (e.code === 'Delete' || e.code === 'Backspace') runEditorShortcut(s.handleDeleteSelected);
      if (e.code === 'KeyG') runEditorShortcut(s.handleJumpToStart);
      if (e.code === 'KeyH') runEditorShortcut(s.handleJumpToPrevious);
      if (e.code === 'KeyL') runEditorShortcut(s.handleJumpToNext);
      if (e.code === 'Semicolon') runEditorShortcut(s.handleJumpToEnd);
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [isEditing]);

  // 用 ref 持有最新 handleClose，避免 effect 依赖 handleClose 导致频繁重新订阅
  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;

  // 移动端不支持编辑模式，进入时或尺寸变为移动端时自动退出
  useEffect(() => {
    if (!isEditing) return;
    const mq = window.matchMedia('(max-width: 768px)');
    if (mq.matches) {
      handleCloseRef.current();
      return;
    }
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) handleCloseRef.current();
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [isEditing]);

  if (!isEditing) return null;

  return (
    <>
      <div
        className="pointer-events-auto absolute right-[100px] bottom-4 left-[100px] z-40 flex flex-col gap-2 rounded-3xl p-2 text-white backdrop-blur-sm backdrop-brightness-30 transition-all duration-300 select-none"
        onContextMenu={(e) => e.preventDefault()}
      >
        <TrackToolbar
          onCapture={handleCapture}
          onUpdateSelected={handleUpdateSelected}
          onDeleteSelected={handleDeleteSelected}
          onDeleteAll={handleDeleteAll}
          onPlayToggle={handlePlayToggle}
          onJumpToStart={handleJumpToStart}
          onJumpToPrevious={handleJumpToPrevious}
          onJumpToNext={handleJumpToNext}
          onJumpToEnd={handleJumpToEnd}
          onOptimize={handleOptimize}
          onExport={() => {
            setPlaying(false);
            setExportOpen(true);
          }}
          onTogglePreview={() => setPreviewOpen(!useRaceTrackStore.getState().isPreviewOpen)}
          onClose={handleClose}
        />
        <TrackTimeline onSeek={handleSeek} onKeyframeSelect={handleKeyframeSelect} />
      </div>

      <TrackExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        onExport={handleExport}
      />
    </>
  );
}
